/* eslint-disable react/prop-types */
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import usePush from '../../hooks/usePush';
import LoadingMessage from '../../components/LoadingMessage/index';
import AuthenticationService from '../../services/authentication-service';

import cookies from '../../util/cookies';
import { setUser } from '../login/duck/actions/login';
import useOnMount from '../../hooks/useOnMount';

/*

When Accessing an organisation via this route, these are the possible flows:

logged in -> redirect to OrganisationAccess -> user in org or admin -> setShortCode -> success -> redirect to chasefeed
logged in -> redirect to OrganisationAccess -> user not in org -> show error with button to login with other user -> login -> redirect to OrganisationAccess -> ...
not logged in -> try login with cookie -> success -> redirect to OrganisationAccess -> setShortCode -> success -> redirect chasefeed
not logged in -> try login with cookie -> fail -> redirect to login -> login -> redirect to OrganisationAccess -> ...

*/

const OrganisationLogin = ({ history, location, match }) => {
  const dispatch = useDispatch();
  const { shortCode } = useParams();
  const { isLoggedIn } = useSelector(state => state.loginReducer);
  const redirect = `${location.pathname.split(match.url)[1]}${location.search ||
    ''}${location.hash || ''}`;
  const { execute } = usePush(AuthenticationService.loginWithToken);

  function toLogin() {
    history.replace('/login', {
      from: { pathname: `${location.pathname}${location.search || ''}${location.hash || ''}` },
    });
  }

  async function loginWithToken(token) {
    try {
      // Make the destructuring more defensive for Vitest compatibility
      const response = await execute(token);

      // Handle both array response [err, result] and direct result formats
      let err, result;
      if (Array.isArray(response)) {
        [err, result] = response;
        if (err) {
          toLogin();
          return;
        }
      } else {
        // If not an array, assume it's the direct result
        result = response;
      }

      // Safely extract user and token
      const user = result?.user;
      const userToken = result?.token;

      // Handle the case where user might be undefined in tests
      if (!user) {
        console.error('Missing user in response');
        toLogin();
        return;
      }

      user.token = userToken;
      dispatch(setUser(user));
      history.replace(`/o/${shortCode}/access`, { redirect });
    } catch (error) {
      console.error('Error during login with token:', error);
      toLogin();
    }
  }

  useOnMount(() => {
    const token = cookies.get('token');
    if (isLoggedIn) {
      history.replace(`/o/${shortCode}/access`, { redirect });
    } else if (token) {
      loginWithToken(token);
    } else {
      toLogin();
    }
  });

  return <LoadingMessage center useLogoAnimation />;
};

export default OrganisationLogin;
